import { ILocalized } from '../../../../../src/locale/types';
import { EGranteeType, EReportActionSet, IGrantee, IInvitableUser } from '@opengov/reporting-types';
import { List, Map } from 'immutable';
import React, { useState } from 'react';

import { IReportLocale } from '../../../locale';
import { styles } from './ManageAccountSummaryAccessModal.style';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  Divider,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReportAccountSummaryAccessGrid } from '../../../../reportSettings/ReportSettings/ReportAccountSummaryAccessGrid';
import { getTextFieldEndAdornment } from '@opengov/capital-mui-theme';
import { Check } from '@mui/icons-material';

export interface IManageAccessModalProps extends ILocalized {
  reportTitle: string;
  currentUserId: string;
  currentGrantees: Map<string, IGrantee>;
  entityId: number;
  temporaryGrantees: Map<string, IGrantee>;
  invitableUsers: List<IInvitableUser>;
  saveAccess(updatedGrantees: Map<string, IGrantee>): void;
  updateTemporaryGrantee(grantee: IGrantee): void;
  closeModal(): void;
  isReportMultiDataset: boolean;
  isModalOpen: boolean;
}

export interface IReactSelectOption {
  isDisabled: boolean;
  label: string;
  meta: string;
  secondaryLabel: string;
  value: string;
}

export interface IAccessSelectOption {
  label: string;
  value: EReportActionSet;
}

export const handleAddGrantees = (
  newGrantees: List<IReactSelectOption>,
  newUserPermission: EReportActionSet,
  setCurrentUsersToAdd: (users: List<IReactSelectOption>) => void,
  updateTemporaryGrantee: (grantee: IGrantee) => void
) => {
  newGrantees.forEach((user) => {
    updateTemporaryGrantee({
      granteeId: user.value,
      granteeName: user.label,
      granteeType: EGranteeType.User,
      actionSet: newUserPermission,
      granteeEmail: user.meta || ''
    });
  });

  setCurrentUsersToAdd(List<IReactSelectOption>([]));
};

export const handleSelectGrantee = (
  addedUsers: IReactSelectOption[],
  setCurrentUsersToAdd: (users: List<IReactSelectOption>) => void
) => {
  setCurrentUsersToAdd(List(addedUsers));
};

type AccessCellRendererParams = {
  value: string | EReportActionSet | undefined;
  row: {
    id: string;
    granteeId: string;
    granteeName: string;
    granteeEmail: string;
    granteeType: EGranteeType;
    actionSet: EReportActionSet;
  };
};

export const accessCellRendererSelector = (
  params: AccessCellRendererParams,
  currentUserId: string,
  entityId: number,
  reportLocaleText: IReportLocale,
  isReportMultiDataset: boolean
) => {
  const newParams = {
    disabled: false,
    selectOptions: [] as Array<IAccessSelectOption>
  };

  // Use granteeId for permission logic, independent of the underlying DB id.
  if (params.row.granteeId === 'entity_admin' || params.row.granteeId === currentUserId) {
    newParams.disabled = true;
  }
  if (params.row.granteeId === `/entity/${entityId}`) {
    newParams.selectOptions = [
      { label: reportLocaleText.manageAccess.noAccess, value: EReportActionSet.ReportNoAccess },
      { label: reportLocaleText.manageAccess.viewer, value: EReportActionSet.ReportViewer }
    ];
  } else {
    newParams.selectOptions = [
      { label: reportLocaleText.manageAccess.viewer, value: EReportActionSet.ReportViewer },
      {
        label: isReportMultiDataset
          ? reportLocaleText.manageAccess.savedViewEditor
          : reportLocaleText.manageAccess.editor,
        value: isReportMultiDataset ? EReportActionSet.ReportSavedViewEditor : EReportActionSet.ReportEditor
      },
      { label: reportLocaleText.manageAccess.owner, value: EReportActionSet.ReportOwner },
      { label: reportLocaleText.manageAccess.remove, value: EReportActionSet.ReportNoAccess }
    ];
  }

  return {
    component: 'SelectCellRenderer',
    params: newParams
  };
};

export const changeAccessHandler = (
  newActionSet: string,
  granteeRow: {
    id: string;
    granteeId: string;
    granteeName: string;
    granteeEmail: string;
    granteeType: EGranteeType;
  },
  updateTemporaryGrantee: (grantee: IGrantee) => void
) => {
  const newGrantee: IGrantee = {
    // If the row id differs from the granteeId, treat it as the DB primary key;
    // otherwise this is a new/default grantee without a persisted grant row yet.
    id: granteeRow.id !== granteeRow.granteeId ? granteeRow.id : undefined,
    granteeId: granteeRow.granteeId,
    granteeName: granteeRow.granteeName,
    granteeEmail: granteeRow.granteeEmail,
    granteeType: granteeRow.granteeType,
    actionSet: newActionSet
  };
  updateTemporaryGrantee(newGrantee);
};

export const handleSaveGrantees = (
  saveAccess: (grantees: Map<string, IGrantee>) => void,
  closeModal,
  temporaryGrantees
) => {
  saveAccess(temporaryGrantees);
  closeModal();
};

export const getColumnDefs = (
  reportLocaleText: IReportLocale,
  currentUserId: string,
  entityId: number,
  sortedColumn: string,
  updateTemporaryGrantee,
  isReportMultiDataset: boolean
) => {
  const width = 280;
  return [
    {
      headerName: reportLocaleText.manageAccess.name,
      field: 'granteeName',
      headerComponentParams: {
        isSorted: sortedColumn === 'granteeName'
      },
      valueGetter: (value: string): string => {
        return value || '-';
      }
    },
    {
      headerName: reportLocaleText.manageAccess.email,
      field: 'granteeEmail',
      headerComponentParams: {
        isSorted: sortedColumn === 'granteeEmail'
      },
      valueGetter: (value: string): string => {
        return value || '-';
      },
      width: width
    },
    {
      headerName: reportLocaleText.manageAccess.access,
      field: 'actionSet',
      headerComponentParams: {
        isSorted: sortedColumn === 'actionSet'
      },
      valueGetter: (value): string => {
        switch (value) {
          case EReportActionSet.ReportViewer:
            return EReportActionSet.ReportViewer;
          case EReportActionSet.ReportEditor:
            return EReportActionSet.ReportEditor;
          case EReportActionSet.ReportSavedViewEditor:
            return EReportActionSet.ReportSavedViewEditor;
          case EReportActionSet.ReportOwner:
            return EReportActionSet.ReportOwner;
          case EReportActionSet.ReportNoAccess:
            return EReportActionSet.ReportNoAccess;
          default:
            return value;
        }
      },
      renderCell: (params: AccessCellRendererParams) => {
        const options = accessCellRendererSelector(
          params,
          currentUserId,
          entityId,
          reportLocaleText,
          isReportMultiDataset
        );
        return (
          <Select
            data-test="access-select"
            sx={styles.userActionSelect}
            value={String(params.value)}
            disabled={options.params.disabled}
            onChange={(event) => {
              changeAccessHandler(event.target.value, params.row, updateTemporaryGrantee);
            }}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            {options.params.selectOptions.map((option: IAccessSelectOption) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      }
    },
    {
      headerName: reportLocaleText.manageAccess.manageAccess,
      field: 'granteeType',
      headerComponentParams: {
        isSorted: sortedColumn === 'granteeType'
      },
      valueGetter: (value: string): string => {
        return value || '-';
      },
      width: width
    }
  ];
};

export const ManageAccountSummaryAccessModal = (props: IManageAccessModalProps) => {
  const {
    reportTitle,
    reportLocaleText,
    currentGrantees,
    temporaryGrantees,
    invitableUsers,
    currentUserId,
    entityId,
    closeModal,
    saveAccess,
    updateTemporaryGrantee,
    isReportMultiDataset,
    isModalOpen
  } = props;
  const [sortedColumn] = useState('');

  const [currentUsersToAdd, setCurrentUsersToAdd] = useState(List<IReactSelectOption>([]));
  const [newUserPermission, setNewUserPermission] = useState(EReportActionSet.ReportViewer);

  const changesMade = !currentGrantees.equals(temporaryGrantees);
  const endAdornment = (status: string) => getTextFieldEndAdornment(status) as JSX.Element;

  const currentInvitableUsers = invitableUsers
    .map((user) => {
      const isAdded = !!temporaryGrantees.find((grantee) => grantee.granteeId === user.granteeId);
      return {
        label: user.granteeName,
        meta: user.granteeEmail,
        value: user.granteeId,
        isDisabled: isAdded || currentUserId === user.granteeId,
        secondaryLabel: isAdded ? 'Added' : ''
      };
    })
    .toArray();

  const toggleUserSelection = (option: IReactSelectOption, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (option.isDisabled) return;

    const isSelected = !!currentUsersToAdd.find((user) => user.value === option.value);
    let newUsers: IReactSelectOption[];

    if (isSelected) {
      // Remove user if already selected
      newUsers = currentUsersToAdd.filter((user) => user.value !== option.value).toArray();
    } else {
      // Add user if not already selected
      newUsers = [...currentUsersToAdd.toArray(), option];
    }

    handleSelectGrantee(newUsers, setCurrentUsersToAdd);
  };

  return (
    <Dialog open={isModalOpen} onClose={closeModal} maxWidth="lg">
      <DialogTitle data-test="view-header">{reportTitle}</DialogTitle>
      <IconButton className="MuiDialog__close-button" aria-label="close" onClick={closeModal}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.modalContent}>
        <Box sx={styles.header}>
          <Typography variant="h2">{reportLocaleText.manageAccess.manageAccess}</Typography>
        </Box>

        <Box sx={styles.filterableInput}>
          <Autocomplete
            sx={styles.addUserAutocomplete}
            data-test="user-search-filterable-input"
            multiple
            limitTags={4}
            disableCloseOnSelect
            options={currentInvitableUsers}
            ListboxProps={{
              sx: styles.listbox
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            placeholder="Select users"
            openOnFocus
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase().trim();
              if (!inputValue) return options;

              return options.filter(
                (option) =>
                  option.label.toLowerCase().includes(inputValue) || option.meta.toLowerCase().includes(inputValue)
              );
            }}
            renderOption={(props, option) => {
              const isSelected = !!currentUsersToAdd.find((user) => user.value === option.value);
              return (
                <ListItem
                  {...props}
                  key={option.value}
                  disablePadding
                  onClick={(event) => {
                    toggleUserSelection(option, event);
                  }}
                >
                  <ListItemButton disabled={option.isDisabled} data-test={`user-search-option-${option.meta}`}>
                    <ListItemIcon>{isSelected && <Check />}</ListItemIcon>
                    <ListItemText primary={option.label} secondary={option.meta} />
                  </ListItemButton>
                </ListItem>
              );
            }}
            value={currentUsersToAdd.toArray()}
            renderInput={(params) => <TextField {...params} label={reportLocaleText.manageAccess.addUsers} />}
            onChange={(e, value) => {
              handleSelectGrantee(value, setCurrentUsersToAdd);
            }}
          />
        </Box>

        <Box sx={styles.buttons}>
          <Select
            size="medium"
            value={newUserPermission}
            onChange={(e) => {
              setNewUserPermission(e.target.value as EReportActionSet);
            }}
            sx={styles.permissionSelection}
            data-test="new-user-permission-select"
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem data-test="new-user-permission-option-viewer" value={EReportActionSet.ReportViewer}>
              {reportLocaleText.manageAccess.viewer}
            </MenuItem>
            <MenuItem
              data-test="new-user-permission-option-editor"
              value={isReportMultiDataset ? EReportActionSet.ReportSavedViewEditor : EReportActionSet.ReportEditor}
            >
              {isReportMultiDataset
                ? reportLocaleText.manageAccess.savedViewEditor
                : reportLocaleText.manageAccess.editor}
            </MenuItem>
            <MenuItem data-test="new-user-permission-option-owner" value={EReportActionSet.ReportOwner}>
              {reportLocaleText.manageAccess.owner}
            </MenuItem>
          </Select>
          <Button
            data-test="add-user-button"
            disabled={currentUsersToAdd.size < 1}
            variant="contained"
            size="medium"
            sx={styles.addUserBtn}
            onClick={() => {
              handleAddGrantees(currentUsersToAdd, newUserPermission, setCurrentUsersToAdd, updateTemporaryGrantee);
            }}
          >
            {reportLocaleText.manageAccess.add}
          </Button>
        </Box>
        <Divider />
        <Typography sx={styles.gridTitle} variant="h3">
          {reportLocaleText.manageAccess.usersAndGroups}
        </Typography>
        <Box sx={styles.tableContainer}>
          <Box sx={styles.gridContainer}>
            <ReportAccountSummaryAccessGrid
              reportLocaleText={reportLocaleText}
              granteeRows={temporaryGrantees}
              columnDefs={getColumnDefs(
                reportLocaleText,
                currentUserId,
                entityId,
                sortedColumn,
                updateTemporaryGrantee,
                isReportMultiDataset
              )}
            />
          </Box>
          {changesMade && (
            <Box sx={styles.changesMadeWrapper}>
              <Typography style={styles.changesMadeWrapperText} variant="bodySmall" data-test="changes-made-text">
                {endAdornment('success')}
                {reportLocaleText.manageAccess.changesMade}
              </Typography>
            </Box>
          )}

          <Box sx={{ ...styles.saveBtnBase, ...(changesMade ? styles.saveBtnWithChanges : {}) }}>
            <Button variant="text" onClick={closeModal} data-test="cancel-manage-access-button">
              {reportLocaleText.manageAccess.cancel}
            </Button>

            <Button
              variant="contained"
              disabled={!changesMade}
              onClick={() => {
                handleSaveGrantees(saveAccess, closeModal, temporaryGrantees);
              }}
              data-test="save-manage-access-button"
              sx={styles.addUserBtn}
            >
              {reportLocaleText.manageAccess.save}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
